import "../styles/globals.css";
import "react-tooltip/dist/react-tooltip.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "@fortawesome/fontawesome-free/js/all.js";
import type { AppProps } from "next/app";
import { AppProviders } from "@/components/AppProviders";
import Layout from "@/components/layout/Layout";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Amplify } from "aws-amplify";

import { getCurrentBaseUrl } from "@/libs/clientConfig";
import TrackingScripts from "@/components/scripts/TrackingScripts";
import { useRouteTracker } from "@/hooks/useRouteTracker";
import { useRuntimeConfig } from "@/hooks/useRuntimeConfig";
import { LoadingScreen } from "@/components/LoadingScreen";
import ErrorBoundary from "@/components/ErrorBoundary";

export default function App({ Component, pageProps }: AppProps) {
  const [queryClient] = useState(() => new QueryClient());
  const [amplifyReady, setAmplifyReady] = useState(false);
  const { config, loading } = useRuntimeConfig();

  useRouteTracker();

  // Lazy initialization of Amplify using runtime config
  useEffect(() => {
    if (config && !amplifyReady) {
      // Configure Amplify with runtime-loaded config
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: config.cognito.userPool,
            userPoolClientId: config.cognito.clientId,
            identityPoolId: config.cognito.identityPool,
            loginWith: {
              oauth: {
                domain: `auth.${getCurrentBaseUrl().replace(
                  /^https?:\/\//,
                  ""
                )}`,
                scopes: ["openid", "email", "profile"],
                redirectSignIn: [`${getCurrentBaseUrl()}/`],
                redirectSignOut: [`${getCurrentBaseUrl()}/`],
                responseType: "code",
              },
            },
          },
        },
      });
      setAmplifyReady(true);
    }
  }, [config, amplifyReady]);

  // Show loading screen while config is loading or Amplify is initializing
  if (loading || !amplifyReady) {
    return <LoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TrackingScripts />
      {/*
        ErrorBoundary is mounted here, inside the `loading` guard above, so `config` is already
        resolved and the Airbrake notifier is constructed with real values rather than the
        runtime-config defaults. Wrapping the page content (not the providers) keeps a render error
        in a page from taking down the shell.
      */}
      <AppProviders clientConfig={pageProps.clientConfig}>
        <Layout>
          <ErrorBoundary
            airbrake={config?.airbrake}
            environment={getCurrentBaseUrl().replace(/^https?:\/\//, "")}
          >
            <Component {...pageProps} />
          </ErrorBoundary>
        </Layout>
      </AppProviders>
    </QueryClientProvider>
  );
}
